import { Tabs } from "@/components";
import { Tooltip } from "@/components/Tooltip";
import Card from "@/components/UI/Card/Index";
import { WithAuth } from "@/hoc";
import { useTreasury } from "@/hooks";
import { DownloadIcon, InfoIcon } from "@/styles/icons";
import {
  copyTextToClipboard,
  formatCurrency,
  formatCurrencyNoBrSign,
} from "@/utils";
import {
  Box,
  Flex,
  GridItem,
  Heading,
  SimpleGrid,
  Skeleton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { IoPaperPlaneOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import RendimentosTable from "./rendimentos-table";
import { RendimentosFilter } from "./rendimentos-filter";
import { ReportModal } from "./modal-relatório";
import { Button, PageHeading } from "barte-design-system";
const TreasuryPage = () => {
  const navigate = useNavigate();
  const {
    isOpen: isHandleDownloadModalOpen,
    onOpen: onHandleDowloadModalOpen,
    onClose: onHandleDowloadModalClose,
  } = useDisclosure();
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  const {
    futurePostings,
    debitAccountAmount,
    redeemAccountAmount,
    provisionedAmount,
    totalAmount,
    isLoadingBalance,
    isLoadingRedeem,
    getBalanceInitialData,
    debitPixKeys,
    activeTab,
    setActiveTab,
    refreshTreasuryData,
    filterState,
    setFilterState,
  } = useTreasury();

  useEffect(() => {
    getBalanceInitialData();
  }, []);

  useEffect(() => {
    refreshTreasuryData(["ledger"]);
  }, [activeTab, filterState]);

  const TabContent = () => {
    return (
      <Flex flexDir="column" w="100%" position="relative">
        <Flex
          align="center"
          gap="4"
          ml="auto"
          position="absolute"
          top="-50px"
          right="0"
        >
          <RendimentosFilter onFilterChange={setFilterState} />
          {(user?.roles?.includes("BANKING_ADMIN") ||
            user?.roles?.includes("BANKING_REPORT")) && (
            <Button
              variant="outline"
              size="sm"
              onClick={onHandleDowloadModalOpen}
              iconLeft={<DownloadIcon size={14} color="currentColor" />}
            >
              Extrair extrato
            </Button>
          )}
        </Flex>
        <RendimentosTable />
      </Flex>
    );
  };
  const tabs = [
    {
      key: "CHECKING_ACCOUNT",
      label: "Conta corrente",
      content: <TabContent />,
    },
    {
      key: "INVESTMENT_ACCOUNT",
      label: "Conta investimento",
      content: <TabContent />,
    },
  ];

  return (
    <>
      <ReportModal
        isOpen={isHandleDownloadModalOpen}
        onClose={onHandleDowloadModalClose}
        accountType={activeTab}
        onCloseSuccess={onHandleDowloadModalClose}
      />
      <Flex direction="column" w="100%" justify="center">
          <PageHeading
            title="Tesouraria"
            primaryActionLabel={
                (user?.roles?.includes("B_SELLER_ADMIN") ||
                    user?.roles?.includes("BANKING_ADMIN") ||
                    user?.roles?.includes("BANKING_PAYMENT") ||
                    user?.roles?.includes("BANKING_PAYMENT_REQUEST")) ? "Pagar contas" : null
            }
            onPrimaryAction={() => navigate("/pagamentos-unicos")}
          />
        <Flex maxWidth="1280px" flexDir="column" width="100%" gap="1rem" py="4">
          <SimpleGrid columns={1} gap="1rem">
            <GridItem colSpan={{ base: 1, md: 1 }}>
              <Card variant="secondary">
                <Flex w="100%" flexDir="column" gap=".4rem">
                  <Flex w="100%" align="center" gap=".4rem">
                    <Text fontSize="1.2rem" fontWeight="400">
                      Saldo total
                    </Text>
                    <Tooltip
                      label="Valor total somando todas as contas, com rendimento líquido de IOF e IR."
                      placement="top"
                    />
                    
                  </Flex>
                  <Flex align="center" gap=".2rem">
                    <Text fontSize="2rem" fontWeight="400">
                      R$
                    </Text>
                    <Skeleton
                      isLoaded={!isLoadingRedeem && !isLoadingBalance}
                    >
                      <Text fontSize="2.6rem" fontWeight="600">
                        {formatCurrencyNoBrSign({ value: totalAmount })}
                      </Text>
                    </Skeleton>
                  </Flex>
                </Flex>
              </Card>
            </GridItem>

            <SimpleGrid w="100%" minChildWidth="312px" gap="1rem">
              <GridItem>
                <Card variant="secondary" minHeight="252px">
                  <Flex w="100%" flexDir="column" gap=".4rem">
                    <Text fontSize=".875rem">Conta corrente</Text>
                    <Skeleton isLoaded={!isLoadingBalance}>
                      <Text fontSize="1.5rem" fontWeight="600">
                        {formatCurrency({ value: debitAccountAmount })}
                      </Text>
                    </Skeleton>
                  </Flex>

                  {futurePostings > 0 && (
                    <Flex align="center" gap=".4rem">
                      <Text fontSize=".75rem">Lançamentos futuros</Text>
                      <Skeleton isLoaded={!isLoadingRedeem}>
                        <Text fontSize=".75rem">
                          {formatCurrency({ value: futurePostings })}
                        </Text>
                      </Skeleton>
                    </Flex>
                  )}
                  {(user?.roles?.includes("B_SELLER_ADMIN") ||
                    user?.roles?.includes("BANKING_ADMIN") ||
                    user?.roles?.includes("BANKING_WALLET")) && (
                    <Button
                      size="base"
                      round="soft"
                      variant="outline"
                      onClick={() => copyTextToClipboard(debitPixKeys[0]?.key)}
                      disabled={debitPixKeys?.length === 0}
                      iconLeft={<IoPaperPlaneOutline />}
                    >
                      Copiar chave pix
                    </Button>
                  )}
                </Card>
              </GridItem>
              {user?.idSeller !== 829 && (
                <GridItem>
                  <Card variant="secondary" minHeight="252px">
                    <Box>
                      <Text fontSize=".875rem">Conta adquirência</Text>
                      <Skeleton isLoaded={!isLoadingRedeem}>
                        <Text fontSize="1.875rem" fontWeight="600">
                          {formatCurrency({ value: redeemAccountAmount })}
                        </Text>
                      </Skeleton>
                    </Box>
                    <Flex align="center" gap=".4rem">
                      <Text fontSize=".75rem">Recebíveis futuros</Text>
                      <Skeleton isLoaded={!isLoadingRedeem}>
                        <Text fontSize=".75rem">
                          {formatCurrency({ value: provisionedAmount })}
                        </Text>
                      </Skeleton>
                    </Flex>
                    <Button
                      variant="link"
                      onClick={() => navigate("/financeiro")}
                    >
                      Mais Detalhes
                    </Button>
                  </Card>
                </GridItem>
              )}
            </SimpleGrid>
          </SimpleGrid>
          <Tabs
            tabs={tabs}
            currentKey={activeTab}
            onChange={(key) => setActiveTab(key)}
          />
        </Flex>
      </Flex>
    </>
  );
};

export default WithAuth(TreasuryPage);
